// src/encounter/note-sign-actions.tsx
//
// The long page's Wrap-up foot (22 Oct, Antonin's review; the export's NoteSignActions, DECISIONS 2026-10-22 "Wrap-up foot is one
// component"): the note-level actions as quiet text buttons with the icon BEFORE the word (View note · Clinical edits · Post-visit
// summary · Discard note) on the left, Send orders (N) and Sign note on the right. Never icon-only. The hairline above it is the
// footer's own (`[data-slot="note-end-sign"]`, showcase-bridge.css).
//
// No logic of its own: every handler, gate, disabled state and reason is the page's, passed in as it was to the Wrap-up action bar
// (`WrapUpNoteActionsProps`, `sendOrdersState`) and to the end-of-page Sign (`headerSign`). An absent slice draws nothing:
//   · no `noteActions` → no note actions; no `onViewPvs` → no Post-visit summary; no `onDiscard` → no Discard (after the provider
//     sign, and never for a nurse);
//   · no `sendOrders` → no Send orders (a nurse, or no Wrap-up status slice);
//   · no `sign` → no Sign note (signed, or no sign slice).
import { Button, Icon } from '@scripsteam/scrips-signal-ds';
import { Signature } from 'lucide-react';
import type { WrapUpNoteActionsProps } from './wrap-up-actions-header';

export interface NoteSignActionsProps {
  noteActions?: WrapUpNoteActionsProps;
  sendOrders?: { label: string; disabled: boolean; title: string; onClick: () => void };
  sign?: { label: string; disabled: boolean; title: string; onSign: () => void };
}

export function NoteSignActions({ noteActions, sendOrders, sign }: NoteSignActionsProps) {
  return (
    <div data-slot="note-sign-actions" className="flex w-full flex-wrap items-center justify-between gap-3">
      {noteActions ? (
        <div role="group" aria-label="Note actions" className="flex flex-wrap items-center gap-1">
          <Button variant="tertiary" size="sm" icon={<Icon name="file" size={16} aria-hidden />} onClick={noteActions.onViewNote}>
            View note
          </Button>
          <Button variant="tertiary" size="sm" icon={<Icon name="clipboard" size={16} aria-hidden />} onClick={noteActions.onClinicalEdit}>
            Clinical edits
          </Button>
          {noteActions.onViewPvs ? (
            <Button variant="tertiary" size="sm" icon={<Icon name="preview" size={16} aria-hidden />} onClick={noteActions.onViewPvs}>
              Post-visit summary
            </Button>
          ) : null}
          {noteActions.onDiscard ? (
            <Button
              variant="tertiary"
              size="sm"
              icon={<Icon name="delete" size={16} aria-hidden />}
              disabled={!!noteActions.discardDisabled}
              onClick={noteActions.onDiscard}
            >
              Discard note
            </Button>
          ) : null}
        </div>
      ) : null}
      {sendOrders || sign ? (
        <div className="ml-auto flex items-center gap-3">
          {sendOrders ? (
            <Button
              variant="secondary"
              disabled={sendOrders.disabled}
              title={sendOrders.disabled ? sendOrders.title : undefined}
              onClick={sendOrders.onClick}
            >
              {sendOrders.label}
            </Button>
          ) : null}
          {sign ? (
            <Button
              variant="primary"
              // The hook the floating sign bar's "Go to Sign" moves to (note-sign-bar.tsx), whatever the label says.
              data-slot="note-sign-button"
              // The signature glyph, as the other actions carry theirs (Antonin 30 Oct); the name stays the label.
              icon={<Signature className="size-4" aria-hidden />}
              disabled={sign.disabled}
              title={sign.disabled ? sign.title : undefined}
              onClick={sign.onSign}
            >
              {sign.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
